import LowPass from '../../models/filter/low-pass';
import HighPass from '../../models/filter/high-pass';
import BandPass from '../../models/filter/band-pass';
import { Complex } from '../process-data/complex';
import FrequencyRange from '../process-data/frequency';

/**
 * Where code knows which filter it wants, it can import that filter and use it directly.
 * The FilterType interface and filters object permit using a variable representing the type of filter.
 * ```
 *  filters[chosenFilterType].getStages(...)
 * ```
 */
export interface FilterType {
  lowPass: typeof LowPass;
  highPass: typeof HighPass;
  bandPass: typeof BandPass;
}

export const filters: FilterType = {
  get highPass() {
    return HighPass;
  },
  get bandPass() {
    return BandPass;
  },
  get lowPass() {
    return LowPass;
  },
};

/**
 * `FilterStage` is the returned value that describes a stage of a filter.
 */
export type FilterStage = {
  filter: typeof LowPass | typeof HighPass | typeof BandPass;
  stageType: string;
  stageLetter: string;
  stageFp: number;
  stageFz: number;
  stageId: number;
};

/**
 * `SpecificationParameters` collects data passed into a filter to perform various calculations
 */
export type SpecificationParameters = {
  filterResponseType: string;
  passbandFrequency: number;
  stopbandFrequency: number;
  passbandAttenuation: number;
  stopbandAttenuation: number;
  centerFrequency: number;
  filterResponseValue: number;
};

/**
 * The step response data returned by the `create{First/Second}OrderStepResponse` filter methods.
 */
export type StepResponse = {
  w0: number;
  Q: number;
  a: number;
  b: number;
  c: number;
};

/**
 * `FilterSignature` is the set of methods that each of the filters deliver
 */
export interface FilterSignature {
  get name(): string;
  getStages: (specificationParameters: SpecificationParameters) => FilterStage[];
  getOrder: (specificationParameters: SpecificationParameters) => number;
  getNumberOfStages: (specificationParameters: SpecificationParameters) => number;
  getFilterFrequencyRange: (
    stopbandFrequency: number,
    passbandFrequency?: number,
    centerFrequency?: number,
  ) => FrequencyRange;
  getActualStageSpecFrequency: (frequencyArray: FrequencyRange | number) => FrequencyRange;
  createFirstOrderFilterAmplitude: (fp?: number, gain?: number, frequencyArray?: number[]) => Complex[] | void;
  createSecondOrderFilterAmplitude: (fp: number, Q: number, gain: number, frequencyArray: number[]) => Complex[];
  createTimeAxis: (passbandFrequency: number, stopbandFrequency?: number) => number[];
  setupFirstOrderStepResponse: (stageFp?: number, gain?: number) => StepResponse | void;
  setupSecondOrderStepResponse: (stageFp: number, stageQ: number, gain: number) => StepResponse;
  getImpulseArrayFirstElement: (gain?: number) => number;
  getSecondOrderStageImplementation: (stageFp?: number, stageQ?: number, gain?: number) => string;
  getMaxFrequency: (stopbandFrequency: number, frequencyArray?: number[], stageFp?: number) => number;
  getAdcFrequency: (stopFrequency: number) => number;
}
